import { useState, useEffect } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  UserGroupIcon,
  ChartPieIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const CentroCustos = () => {
  const [centros, setCentros] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('');

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      ativo: true
    }
  });

  useEffect(() => {
    carregarCentros();
  }, []);

  const carregarCentros = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/centro-custos');
      setCentros(response.data);
    } catch (error) {
      console.error('Erro ao carregar centros de custo:', error);
      toast.error('Erro ao carregar centros de custo');
    } finally {
      setCarregando(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (editingId) {
        await axios.put(`/api/centro-custos/${editingId}`, data);
        toast.success('Centro de custo atualizado com sucesso!');
      } else {
        await axios.post('/api/centro-custos', data);
        toast.success('Centro de custo cadastrado com sucesso!');
      }
      
      setShowModal(false);
      reset();
      setEditingId(null);
      carregarCentros();
    } catch (error) {
      console.error('Erro ao salvar centro de custo:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar centro de custo');
    }
  };

  const handleEdit = (centro) => {
    reset(centro);
    setEditingId(centro.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este centro de custo?')) {
      return;
    }

    try {
      await axios.delete(`/api/centro-custos/${id}`);
      toast.success('Centro de custo excluído com sucesso!');
      carregarCentros();
    } catch (error) {
      console.error('Erro ao excluir centro de custo:', error);
      toast.error('Erro ao excluir centro de custo');
    }
  };

  const toggleStatus = async (id, ativo) => {
    try {
      await axios.patch(`/api/centro-custos/${id}`, { ativo: !ativo });
      toast.success('Status do centro de custo atualizado!');
      carregarCentros();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status do centro de custo');
    }
  };

  // Filtrar centros
  const centrosFiltrados = centros.filter(centro => 
    filtro === '' || 
    centro.codigo.toLowerCase().includes(filtro.toLowerCase()) ||
    centro.descricao.toLowerCase().includes(filtro.toLowerCase()) ||
    (centro.responsavel && centro.responsavel.toLowerCase().includes(filtro.toLowerCase()))
  );

  const centrosAtivos = centros.filter(c => c.ativo).length;

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">🏷️ Centros de Custo</h1>
          <p className="text-gray-600">Gerencie os departamentos e áreas da empresa</p>
        </div>
        <button
          onClick={() => {
            reset();
            setEditingId(null);
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Novo Centro
        </button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <ChartPieIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Centros</p>
                <p className="text-2xl font-bold text-gray-900">{centros.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-success-100">
                <CheckCircleIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Centros Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{centrosAtivos}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-gray-100">
                <UserGroupIcon className="h-6 w-6 text-gray-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Com Responsável</p>
                <p className="text-2xl font-bold text-gray-900">
                  {centros.filter(c => c.responsavel).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="w-full md:w-1/2">
              <div className="form-group">
                <label className="form-label">
                  <MagnifyingGlassIcon className="h-4 w-4 inline mr-1" />
                  Buscar Centro de Custo
                </label>
                <input
                  type="text"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  className="form-input"
                  placeholder="Código, descrição ou responsável..."
                />
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setFiltro('')}
                className="btn-secondary"
              >
                Limpar Busca
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Centros de Custo */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Lista de Centros de Custo ({centrosFiltrados.length})
          </h3>
          {carregando && (
            <div className="loader h-6 w-6"></div>
          )}
        </div>
        
        <div className="card-body p-0">
          {centrosFiltrados.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Descrição</th>
                    <th>Responsável</th>
                    <th>Status</th>
                    <th>Cadastro</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {centrosFiltrados.map((centro) => (
                    <tr key={centro.id}>
                      <td>
                        <div className="flex items-center">
                          <ChartPieIcon className="h-5 w-5 text-primary-500 mr-2" />
                          <code className="font-mono font-bold">{centro.codigo}</code>
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="font-medium">{centro.descricao}</p>
                          <p className="text-xs text-gray-500">ID: {centro.id}</p>
                        </div>
                      </td>
                      <td>
                        {centro.responsavel ? (
                          <div className="flex items-center">
                            <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span>{centro.responsavel}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">Não definido</span>
                        )}
                      </td>
                      <td>
                        <button
                          onClick={() => toggleStatus(centro.id, centro.ativo)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            centro.ativo 
                              ? 'bg-success-100 text-success-800 hover:bg-success-200' 
                              : 'bg-danger-100 text-danger-800 hover:bg-danger-200'
                          }`}
                        >
                          {centro.ativo ? (
                            <>
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Ativo
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="h-3 w-3 mr-1" />
                              Inativo
                            </>
                          )}
                        </button>
                      </td>
                      <td>
                        <span className="text-sm text-gray-600">
                          {new Date(centro.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </td>
                      <td>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(centro)}
                            className="text-primary-600 hover:text-primary-900 p-1"
                            title="Editar"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(centro.id)}
                            className="text-danger-600 hover:text-danger-900 p-1"
                            title="Excluir"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <ChartPieIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-lg font-medium">Nenhum centro de custo encontrado</p>
              <p className="text-sm mt-1">
                {filtro 
                  ? 'Nenhum resultado para a busca realizada' 
                  : 'Cadastre seu primeiro centro de custo'}
              </p>
              {filtro ? (
                <button
                  onClick={() => setFiltro('')}
                  className="btn-primary mt-4"
                >
                  Limpar Busca
                </button>
              ) : (
                <button
                  onClick={() => setShowModal(true)}
                  className="btn-primary mt-4"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Cadastrar Primeiro Centro
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Centro de Custo */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingId ? '✏️ Editar Centro de Custo' : '➕ Novo Centro de Custo'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">Código *</label>
                  <input
                    type="text"
                    {...register('codigo', { 
                      required: 'Código é obrigatório',
                      pattern: {
                        value: /^[A-Z0-9]+$/,
                        message: 'Use apenas letras maiúsculas e números'
                      }
                    })}
                    className="form-input"
                    placeholder="Ex: CC001, VENDAS"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Identificador único do centro de custo
                  </p>
                  {errors.codigo && (
                    <p className="form-error">{errors.codigo.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Descrição *</label>
                  <input
                    type="text"
                    {...register('descricao', { 
                      required: 'Descrição é obrigatória',
                      minLength: {
                        value: 3,
                        message: 'Mínimo 3 caracteres'
                      }
                    })}
                    className="form-input"
                    placeholder="Ex: Vendas, Produção, Administrativo"
                  />
                  {errors.descricao && (
                    <p className="form-error">{errors.descricao.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Responsável</label>
                  <input
                    type="text"
                    {...register('responsavel')}
                    className="form-input"
                    placeholder="Nome do responsável"
                  />
                </div>

                <div className="form-group">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('ativo')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Centro de custo ativo</span>
                  </label>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    reset();
                    setEditingId(null);
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingId ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CentroCustos;